import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { COMPANY } from "@/data";

const Confidentialite = () => (
  <>
    <SEO
      title="Politique de Confidentialité"
      description="Politique de confidentialité et protection des données personnelles de VistaWash. Conformité RGPD."
      path="/politique-confidentialite"
    />

    <section className="pt-40 pb-16 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Vos données, notre engagement</span>
          <h1 className="font-impact text-5xl md:text-7xl lg:text-8xl leading-[0.9] mt-6">
            POLITIQUE DE <br />
            <span className="text-[#D90000]">CONFIDENTIALITÉ.</span>
          </h1>
          <p className="text-white/60 leading-relaxed mt-8 max-w-2xl">
            VistaWash s'engage à protéger vos données personnelles conformément au Règlement Général sur la Protection des Données (RGPD - UE 2016/679) et à la loi belge du 30 juillet 2018.
          </p>
        </motion.div>
      </div>
    </section>

    <section className="pb-32 px-6 md:px-12">
      <div className="max-w-4xl mx-auto space-y-16">
        <Block number="01" title="Responsable du traitement">
          <p>
            Le responsable du traitement des données est <strong className="text-white">VistaWash</strong>, {COMPANY.address_line1}, {COMPANY.address_line2}.
          </p>
          <p>Contact : <a href={`mailto:${COMPANY.email}`} className="text-[#D90000] hover:underline">{COMPANY.email}</a></p>
        </Block>

        <Block number="02" title="Données collectées">
          <p>Dans le cadre du formulaire de demande de devis, nous collectons uniquement les données nécessaires :</p>
          <ul className="list-none space-y-2 mt-3">
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Nom et prénom</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Adresse email</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Numéro de téléphone</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Type de véhicule</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Message (optionnel)</li>
          </ul>
        </Block>

        <Block number="03" title="Finalités du traitement">
          <p>Vos données sont utilisées exclusivement pour :</p>
          <ul className="list-none space-y-2 mt-3">
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Répondre à votre demande de devis</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Vous recontacter par téléphone, WhatsApp ou email</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> Gérer notre relation commerciale</li>
          </ul>
          <p className="mt-4">Aucune donnée n'est utilisée à des fins publicitaires, ni transmise à des tiers commerciaux.</p>
        </Block>

        <Block number="04" title="Base légale">
          <p>
            Le traitement de vos données repose sur votre <strong className="text-white">consentement</strong> (validation du formulaire) et sur notre <strong className="text-white">intérêt légitime</strong> à répondre à vos demandes commerciales.
          </p>
        </Block>

        <Block number="05" title="Durée de conservation">
          <p>Vos données sont conservées pendant :</p>
          <ul className="list-none space-y-2 mt-3">
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">3 ans</strong> après le dernier contact pour les prospects</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">10 ans</strong> pour les clients (obligations comptables belges)</li>
          </ul>
        </Block>

        <Block number="06" title="Vos droits">
          <p>Conformément au RGPD, vous disposez à tout moment des droits suivants :</p>
          <ul className="list-none space-y-2 mt-3">
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit d'accès</strong> à vos données</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit de rectification</strong> en cas d'erreur</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit à l'effacement</strong> (droit à l'oubli)</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit à la limitation</strong> du traitement</li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit à la portabilité</strong></li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit d'opposition</strong></li>
            <li className="flex gap-3"><span className="text-[#D90000]">→</span> <strong className="text-white">Droit de retirer votre consentement</strong> à tout moment</li>
          </ul>
          <p className="mt-4">
            Pour exercer vos droits, contactez-nous à : <a href={`mailto:${COMPANY.email}`} className="text-[#D90000] hover:underline">{COMPANY.email}</a>
          </p>
        </Block>

        <Block number="07" title="Cookies">
          <p>
            Notre site utilise des cookies techniques nécessaires à son fonctionnement et, avec votre consentement, des cookies de mesure d'audience (Google Analytics) pour améliorer votre expérience.
          </p>
          <p className="mt-3">
            Vous pouvez à tout moment gérer vos préférences via la bannière cookies affichée lors de votre première visite, ou en cliquant sur le lien "Gérer les cookies" en bas de page.
          </p>
          <button
            type="button"
            onClick={() => {
              localStorage.removeItem("vw_cookie_consent");
              window.location.reload();
            }}
            data-testid="reset-cookie-consent"
            className="mt-6 inline-flex items-center gap-3 border border-[#D90000] hover:bg-[#D90000] text-white text-[11px] uppercase tracking-[0.25em] font-bold px-6 py-3 transition-colors"
          >
            Gérer les cookies
          </button>
        </Block>

        <Block number="08" title="Sécurité">
          <p>
            Vos données sont hébergées sur des serveurs sécurisés, protégés par des mesures techniques et organisationnelles appropriées : chiffrement HTTPS, accès restreint, sauvegardes régulières.
          </p>
        </Block>

        <Block number="09" title="Réclamation">
          <p>
            Si vous estimez que le traitement de vos données ne respecte pas la réglementation, vous pouvez introduire une réclamation auprès de l'Autorité de protection des données belge :
          </p>
          <p className="mt-3">
            <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-[#D90000] hover:underline">
              www.autoriteprotectiondonnees.be
            </a>
          </p>
        </Block>
      </div>
    </section>
  </>
);

const Block = ({ number, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    data-testid={`privacy-block-${number}`}
    className="border-l-2 border-[#D90000] pl-8"
  >
    <div className="flex items-baseline gap-4 mb-6">
      <span className="font-impact text-2xl text-[#D90000]">{number}</span>
      <h2 className="font-impact text-2xl md:text-3xl uppercase tracking-wide">{title}</h2>
    </div>
    <div className="space-y-3 text-white/70 leading-relaxed">{children}</div>
  </motion.div>
);

export default Confidentialite;
